export type UserRole = 'default' | 'user' | 'admin' | 'service' | 'developer';

export enum GatewayCommand {
    Unknown,
    Shutdown,
    Reboot,
    RestartWorker,
    RestartSupervisor,
    RestartRTSP,
    RestartSip,
    RestartUART
}

export interface UserNotificationSettings {
    alarm: boolean;
    intercom: boolean;
    bell: boolean;
    intertext: boolean;

    security: boolean;
    aitips: boolean;
}

export interface UserModel {
    id: string;
    username: string;
    role: UserRole;
    mobile_os?: string;
    push_token?: string;
    notification_settings: UserNotificationSettings;
    phone_number: string
    first_login?: boolean
    authority_group_id?: string
    temporary_user?: TemporaryUser
    phone_number_verified?: boolean
}

export interface TemporaryUser {
    is_temporary_user: boolean
    is_inactive: boolean
    start_timestamp: number
    end_timestamp: number
}

export interface GatewayInfoModel {
    id: string;
    name: string;
}

export interface UpdateUserModel extends UserModel {
    password: string;
    alarm_password: string;
}

export type OrderControlParameters = {
    order: number
}
export interface MediaModel<T = unknown> {
    id: string;
    name: string;
    bridge_device_id: string;
    icon: string;
    type_id: string;
    start_slot: number;
    button_list: MediaDeviceButton[];
    state: T;
    area_id?: string
}

export type RemoteControlModel = {
    is_remote_control: boolean
}

export type ExternalAlarmParameters = {
    alarm_set_value: number
}
export type SensorEnergyParameters = {
    alarm_disable_value: number
}

export type DeviceParameters = (IOControlParameters | GlassControlParameters);

export type ControlParameters =
    | ThreePhaseControlParameters
    | PulseControlParameters
    | SirenControlParameters
    | AlarmControlParameters
    | SimpleControlParameters
    | DimmerControlParameters
    | ControlThreeAnalogMap
    | RgbControlParameters
    | ControlSoundAnalog
    | ControlScreenSaver
    | ControlFancoilSet
    | ControlNotificationTime
    | KNXLampParameters
    | KNXCurtainParameters
    | KNXPlugParameters
    | KNXDimmerParameters
    | KNXThermostatParameters
    | VRFParameters
    | KNXFancoilParameters
    | RealtimeEnergy
    | HideControlParameters
    | RemoteControlModel
    | PasswordControlParameters
    | OrderControlParameters
    | SensorEnergyParameters
    | ExternalAlarmParameters

export const CONTROL_TYPE_ID_ENERGY = "200"
export const CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON = '202';
export const CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN = '203';
export const CONTROL_TYPE_ID_CAMERA_ENERGY_RELAY = "204"
export const CONTROL_TYPE_ID_LIGHT = '1';
export const CONTROL_TYPE_ID_DIMMER = '2';
export const CONTROL_TYPE_ID_ANALOG = '22';
export const CONTROL_TYPE_ID_BELL = "28"
export const CONTROL_TYPE_ID_DIMMER_COLOR = '23';
export const CONTROL_TYPE_ID_CURTAIN = '3';
export const CONTROL_TYPE_ID_PLUG = '4';
export const CONTROL_TYPE_ID_ALARM = '6';
export const CONTROL_TYPE_ID_THERMOSTAT = '7';
export const CONTROL_TYPE_ID_FANCOIL = "14"
export const CONTROL_TYPE_ID_VALVE = '9';
export const CONTROL_TYPE_ID_CONTACTOR = '10';
export const CONTROL_TYPE_ID_ELEVATOR = '11';
export const CONTROL_TYPE_ID_AIR_CONDITIONER = '13';
export const CONTROL_TYPE_ID_SIREN = '15';
export const CONTROL_TYPE_ID_MKK = '19';
export const CONTROL_TYPE_ID_THREE_ANALOG = '23';
export const CONTROL_TYPE_SCREEN_SAVER = '500';
export const CONTROL_TYPE_GENERAL_TEMPERATURE = "71"
export const DIMMER_VALUE_OPEN = 80;
export const CURTAIN_VALUE_CLOSE = 2;
export const ALARM_VALUE_HOME = 2;
export const ALARM_VALUE_OUTSIDE = 3;
export const CONTROL_TYPE_ID_VRF = "27"
export const CONTROL_TYPE_ID_SENSOR_ENERGY = "205";
export const CONTROL_TYPE_ID_EXTERNAL_ALARM = "206"

export const MEDIA_TYPE_ID_AIR_CONDITIONER = '1';
export const CONTROL_TYPE_ADV_THERMOSTAT = "70"
export const BRIDGE_DEVICE_TYPE_ID_INO_PAN = '11';
export const BRIDGE_DEVICE_TYPE_ID_INO_PAN_2 = '12';
export const BRIDGE_DEVICE_TYPE_ID_INO_PAN_3 = '20';
export const BRIDGE_DEVICE_TYPE_ID_STPM = '5';
export const BRIDGE_DEVICE_TYPE_ID_STPM_F = '10';
export const BRIDGE_DEVICE_TYPE_ID_STS_PLUS = '23';
export const BRIDGE_DEVICE_TYPE_ID_ST = '6';
export const BRIDGE_DEVICE_TYPE_ID_STP = "4"
export const SOUND_LEVEL_TYPE_ID = '401';
export const SOUND_PLAYED_TYPE_ID = '402';
export const BRIDGE_DEVICE_TYPE_ID_CCM = '2'
export const BRIDGE_DEVICE_TYPE_ID_CCM2312 = '25'
export const BRIDGE_DEVICE_TYPE_ID_CCM2324 = "26"
export const BRIDGE_DEVICE_TYPE_ID_EIO = "101"
export const BRIDGE_DEVICE_TYPE_ID_VRF = "27"
export const BRIDGE_DEVICE_TYPE_ID_KNX = "201"
export const BRIDGE_DEVICE_TYPE_ID_KEYPAD = "32"
export const BRIDGE_DEVICE_TYPE_ID_FRAME = "31"
export const BRIDGE_DEVICE_TYPE_ID_ICM = "3"

export interface ControlModel {
    isUpdating?: boolean;
    hasError?: boolean;
    id: string;
    name: string;
    type_id: string;
    bridge_device_id: string;
    camera_id?: string;
    area_id: string;
    rgb_value?: ControlThreeAnalog;

    //rgb_value_map?: ControlThreeAnalogMap;
    current_value?: number;
    temperature_settings?: TemperatureControlSettings;
    parameters: DeviceParameters & ControlParameters;
    is_active?: boolean;
    is_notification?: boolean;
    // down_output_number			`int`		three_phase				all     ThreePhaseControlParameters
    // output_time_limit			`int`		three_phase				all     ThreePhaseControlParameters
    // pulse_time					`int`		pulse					io      PulseControlParameters
    // should_remember_last_value	`bool`		!complex[alarm]			io      IOControlParameters
    // default_value				`int`		!complex[alarm]			io      IOControlParameters
    // should_output_reverse		`bool`		all						io      IOControlParameters
    // control_id					`string`	complex[alarm]			io      AlarmControlParameters
    // siren_type					`int`		complex[alarm]			io      AlarmControlParameters
    // alarm_level					`int`		complex[alarm, siren]	io      SirenControlParameters
    // delay_time					`int`		complex[alarm]			io      AlarmControlParameters
    // max_limit					`int`		complex[dimmer]			all     DimmerControlParameters
    // min_limit					`int`		complex[dimmer]			all     DimmerControlParameters
    // page_number					`int`		all						glass   GlassControlParameters
    // row_number					`int`		all						glass   GlassControlParameters
    // should_output_reverse		`bool`		basic					glass   BasicGlassControlParameters
}

export type RealtimeEnergy = {
    realtime_energy: string
}

export type VRFParameters = {
    unit_id?: number;
}

export type HideControlParameters = {
    hide_control: boolean
}

export type PasswordControlParameters = {
    password_control: boolean
}

export type IOControlParameters = {
    output_number: number;
    should_output_reverse: boolean;
    should_remember_last_value: boolean;
    default_value: number;
};

export type KNXLampParameters = {
    lamp_feedback_address: string
    lamp_output_address: string
}

export type KNXPlugParameters = {
    plug_feedback_address: string
    plug_output_address: string
}

export type KNXDimmerParameters = {
    dimmer_feedback_address: string
    dimmer_output_address: string
}

export type KNXCurtainParameters = {
    blinds_stop_address: string
    blinds_up_down_address: string
}

export type KNXThermostatParameters = {
    thermostat_cooling_mode_feedback_address?: string
    thermostat_heating_mode_feedback_address?: string
    thermostat_current_setpoint_feedback_address: string
    thermostat_current_temp_feedback_address: string
    thermostat_mode_feedback_address: string
    thermostat_mode_output_address: string
    thermostat_setpoint_output_address: string
}

export type KNXFancoilParameters = {
    fancoil_heating_mode_fan_feedback_address: string
    fancoil_cooling_mode_fan_feedback_address: string
    fancoil_current_setpoint_feedback_address: string
    fancoil_current_setpoint_output_address: string
    fancoil_fan_auto_fan_feedback_address: string
    fancoil_current_temp_feedback_address: string
    fancoil_fan_auto_fan_output_address: string
    fancoil_fan_speed_feedback_address: string
    fancoil_fan_speed_output_address: string
    fancoil_status_feedback_address: string
    fancoil_status_output_address: string
    fancoil_mode_feedback_address: string
    fancoil_mode_output_address: string
    control_mode_status: boolean
    control_power_status: boolean
    control_running_status: boolean
    fan_status: string
}

export type GlassControlParameters = {
    output_number: number;
    button_number: number;
};

export type ThreePhaseControlParameters = {
    output_time_limit: number;
    down_output_number: number;
};

export type PulseControlParameters = {
    pulse_time: number;
};

export type SirenControlParameters = {
    alarm_level: number;
};

export type RgbControlParameters = {
   rgb_output: number;
};

export type AlarmControlParameters = {
    siren_type: number;
    delay_time: number;
    relational_control_id: string;
    is_virtual_control: boolean;
} & SirenControlParameters;

export type SimpleControlParameters = {
    should_output_reverse: boolean;
};

export type DimmerControlParameters = {
    max_limit: number;
    min_limit: number;
};

export type ControlThreeAnalog = {
    red: number;
    green: number;
    blue: number;
}

export type ControlThreeAnalogMap = {
    map_r: number;
    map_g: number;
    map_b: number;
}


export type ControlSoundAnalog = {
    sound_type: number;
}

export type ControlScreenSaver = {
    screen_type: number;
}

export type ControlFancoilSet = {
    output_number: number
    fan_output_low: number
    fan_output_medium: number
    fan_output_high: number
    fan_type?: number
}

export type ControlNotificationTime = {
    is_notification: boolean;
    start_time: string;
    end_time: string
}

export type SimpleSet = {
    active: string;
    passive: string;
};

export type ThreePhaseSet = {
    up: string;
    down: string;
    stop: string;
};

export type ThermostatImageSet = {
    cold_on: string;
    cold_off: string;
    heat_on: string;
    heat_off: string;
};

export type ImageSet<T = SimpleSet> = {
    default: string;
    [key: string]: string;
} & T;

export type ControlRenderType = 'complex' | 'simple' | 'three_phase' | 'two_phase' | 'pulse';

export interface ControlTypeModel<T = SimpleSet> {
    id: string;
    name: string;
    render_type: ControlRenderType;
    image_set: ImageSet<T>;
    show_on_scenario: boolean;
    show_on_homepage: boolean;
    ask_value_in_initialize: boolean;
    glass_device_internal_icon_id: number;
    allowed_bridge_device_type_id_list: string[];
    show_on_frame_ui: boolean
}

export interface CameraModel {
    id: string;
    name: string;
    camera_brand_id: string;
    area_id: string;
    ip: string;
    username?: string;
    password?: string;
    is_door_bell?: boolean;

    rtsp?: CameraConnectionConfigModel;
    video_cgi?: CameraConnectionConfigModel;
    video?: CameraConnectionConfigModel;
    video_iframe?: CameraConnectionConfigModel;
    mjpeg?: CameraConnectionConfigModel;
    resolution?: CameraResolution;
    is_rtsp_v2?: boolean
    is_snapshot_v2?: boolean;
}

export interface CameraConnectionConfigModel {
    port: number;
    remote_port: number;
}

export interface CallListModel {
    id: string
    button: string
    sought_ip: string
}

export interface CameraResolution {
    camera_id: string
    height: string
    width: string
}



export interface CameraBrandModel {
    id: string;
    name: string;
    is_door_bell: boolean;
    is_usb: boolean;
    authentication_required: boolean;
    rtsp?: RTSPConfigModel;
    video_cgi?: VideoCGIConfigModel;
    video?: VideoConfigModel;
    video_iframe?: VideoIframeConfigModel;
    mjpeg?: MJPEGConfigModel;
    resolution?: CameraResolution;
}

export interface MJPEGConfigModel {
    stream: StreamConfigModel;
    delay_ms: number;
    is_touch_bell?: boolean
}

export interface VideoCGIConfigModel {
    stream: StreamConfigModel;
}

export interface VideoIframeConfigModel {
    stream: StreamConfigModel;
}

export interface VideoConfigModel {
    stream: StreamConfigModel;
}

export interface RTSPConfigModel {
    stream: StreamConfigModel;
}

export interface StreamConfigModel {
    q_360p?: string;
    q_720p?: string;
}

export type InputPrimitiveType = 'momentary' | 'latch' | 'absolute' | 'sensor';

export interface InputTypeModel {
    id: string;
    name: string;
    primitive_name: InputPrimitiveType;
    registered_by_system?: boolean;
}

export const INPUT_TYPE_ID_MOMENTARY = '0';
export const INPUT_TYPE_ID_LATCH = '1';
export const INPUT_TYPE_ID_ABSOLUTE = '2';
export const INPUT_TYPE_ID_SCENARIO = '3';
export const INPUT_TYPE_ID_SENSOR = '4';
export const INPUT_TYPE_ID_ADV_TEMPERATURE = "6"
export const INPUT_TYPE_ID_KEYPAD = "8"


export interface InputModel {
    isUpdating?: boolean;
    hasError?: boolean;

    id: string;
    name: string;
    type_id: string;
    bridge_device_id: string;
    camera_id?: string;
    input_number: number;
    is_active: boolean;
    is_notification?: boolean;
    relational_id: string;
    is_virtual_control: boolean;
    direction?: number;
    should_output_reverse?: boolean;
    sensor_settings?: SensorSettings;
    registered_by_system?: boolean;
    remote_input?: boolean
    offset_for_adv_thermostat?: number
    is_sensor_saved?: boolean
    keypad_settings?: {
        pin: string
    }
    knx_settings?: {
        address: string
    }
}

export interface SensorTypeModel {
    id: string;
    name: string;
    image_set: ImageSet;
}

export interface SensorSettings {
    sensor_type_id: string;
    value?: boolean;
    order?: number;
}

export interface TemperatureControlSettings {
    whole?: number;
    fraction?: number;
    room_whole?: number;
    room_fraction?: number;
    sensor_slot?: number;
    bridge_device_id: string;
    is_mode_heating: boolean;
    has_heating?: boolean;
    has_cooling?: boolean;
}

export interface MediaDeviceButton {
    slot: number;
    registered: boolean;
}

export enum AirMode {
    Auto,
    Dry,
    Fan,
    Cold,
    Hot,
}

export interface AirConditionerState {
    open: boolean;
    mode: AirMode;
    temperature: number;
    fan_speed: number;
    flap_speed: number;
}

export interface BaseModel {
    id: string;
    type_id?: string;
    registered_by_system?: boolean;
}

export interface RelationModel {
    relational_id: string;
    type: string;
    should_update: boolean;
}
